<template>
    <div
        v-if="show"
        class="all-design-preview-overlay"
        aria-modal="true"
        role="dialog"
        tabindex="-1"
        @click.self="$emit('close')"
    >
        <div class="modal-dialog modal-dialog-centered modal-dialog-scrollable all-design-preview-dialog">
            <div class="modal-content">
                <div class="modal-header d-flex justify-content-between align-items-center">
                    <h5 class="modal-title mb-0">Preview</h5>
                    <button
                        type="button"
                        class="close"
                        aria-label="Close"
                        @click="$emit('close')"
                    >
                        <span aria-hidden="true">&times;</span>
                    </button>
                </div>
                <div class="modal-body all-design-preview-body">
                    <div
                        v-if="loading"
                        class="text-center py-5"
                    >
                        Generating preview…
                    </div>
                    <div
                        v-else
                        class="all-design-preview-grid"
                    >
                        <div
                            v-for="img in images"
                            :key="img.label"
                            class="all-design-preview-item text-center all-design-preview-item-clickable"
                            role="button"
                            tabindex="0"
                            @click="onSelectLocation(img.label)"
                            @keydown.enter.prevent="onSelectLocation(img.label)"
                            @keydown.space.prevent="onSelectLocation(img.label)"
                        >
                            <div class="text-uppercase small font-weight-bold mb-2">
                                {{ displayLabel(img.label) }}
                            </div>
                            <div
                                class="all-design-preview-img-wrap"
                                :style="imageWrapperStyle(img)"
                            >
                                <img
                                    v-if="img.url"
                                    :src="img.url"
                                    :alt="img.label"
                                    class="img-fluid border rounded all-design-preview-img"
                                />
                                <div
                                    v-else
                                    class="all-design-preview-placeholder"
                                >
                                    <span class="text-muted small">No design</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: "AllDesignPreviewModal",
    props: {
        show: {
            type: Boolean,
            default: false
        },
        loading: {
            type: Boolean,
            default: false
        },
        images: {
            type: Array,
            default: () => []
        },
        productColor: {
            type: String,
            default: "#ffffff"
        }
    },
    watch: {
        show(isOpen) {
            if (isOpen) {
                document.body.style.overflow = "hidden";
            } else {
                document.body.style.overflow = "";
            }
        }
    },
    beforeDestroy() {
        document.body.style.overflow = "";
    },
    methods: {
        onSelectLocation(label) {
            if (!label) return;
            const location = (typeof label === "string" ? label : "").toLowerCase().trim();
            if (["front", "back", "left", "right"].includes(location)) {
                this.$emit("select-location", location);
            }
        },
        displayLabel(label) {
            if (!label) return "";
            return label.charAt(0).toUpperCase() + label.slice(1);
        },
        imageWrapperStyle(img) {
            if (!img || !img.label) return {};
            // Show product-color rectangle for any side (front, back, left, right) when:
            // - no design area image (like left/right), OR
            // - use_custom_box is true for this side (custom box => rectangle)
            const useCustomBox = img.useCustomBox === true;
            if (img.hasDesignAreaImage && !useCustomBox) return {};
            const bg = this.productColor || "#ffffff";
            return {
                backgroundColor: bg
            };
        }
    }
};
</script>

<style scoped>
/* Full-screen overlay so background does not scroll or receive clicks */
.all-design-preview-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.5);
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow-y: auto;
    padding: 1rem;
}

.all-design-preview-dialog {
    max-width: 640px;
    margin: auto;
}

.all-design-preview-body {
    min-height: 320px;
    max-height: calc(100vh - 120px);
    overflow-y: auto;
}

.all-design-preview-grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
    justify-items: center;
}

.all-design-preview-item {
    width: 100%;
    max-width: 280px;
    padding: 0.75rem;
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
}

.all-design-preview-item-clickable {
    cursor: pointer;
    transition: border-color 0.2s, box-shadow 0.2s;
}

.all-design-preview-item-clickable:hover {
    border-color: var(--primary, #0d6efd);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Padding around image inside each preview box */
.all-design-preview-img-wrap {
    padding: 0.75rem;
    border-radius: 6px;
}

.all-design-preview-img {
    width: 100%;
    max-height: 320px;
    min-height: 120px;
    object-fit: contain;
    display: block;
}

/* Placeholder when no design: show product color as background */
.all-design-preview-placeholder {
    width: 100%;
    min-height: 180px;
    max-height: 320px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.border{
    border: none !important;
}
</style>
